import React, { useState, useEffect, useMemo } from 'react';
import AirflowPipelineModal from './AirflowPipelineModal';
import HowItWorksModal from './HowItWorksModal';
import { InventoryDateSummary, InventoryUserEntry } from '../types/inventory';
import { formatInventoryDate } from '../utils/date';

interface HeaderProps {
  selectedUserId: string;
  selectedDate: string;
  onUserChange: (userId: string) => void;
  onDateChange: (date: string) => void;
  userName?: string;
  formattedDate?: string;
  onInsightsReload?: () => void;
  availableDates?: InventoryDateSummary[];
  availableUsers?: InventoryUserEntry[];
  isLoadingDates?: boolean;
  isLoadingUsers?: boolean;
  datesError?: string;
  usersError?: string;
  inventoryMeta?: {
    datesGeneratedAt?: string;
    usersGeneratedAt?: string;
    userCount?: number;
  };
}


const Header: React.FC<HeaderProps> = ({
  selectedUserId,
  selectedDate,
  onUserChange,
  onDateChange,
  userName,
  formattedDate,
  onInsightsReload,
  availableDates = [],
  availableUsers = [],
  isLoadingDates = false,
  isLoadingUsers = false,
  datesError,
  usersError,
  inventoryMeta,
}) => {
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [dateChanged, setDateChanged] = useState(false);

  const formatDateLabel = (date: string) => formatInventoryDate(date);

  const formatTimestamp = (isoTimestamp?: string) => {
    if (!isoTimestamp) return '—';
    const dateObj = new Date(isoTimestamp);
    if (Number.isNaN(dateObj.getTime())) {
      return isoTimestamp;
    }
    return dateObj.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const userLabelMap = useMemo(() => new Map(
    availableUsers.map((entry) => [entry.user_id, entry.user_id])
  ), [availableUsers]);

  // Trigger animation when date changes
  useEffect(() => {
    if (selectedDate) {
      setDateChanged(true);
      const timer = setTimeout(() => setDateChanged(false), 600); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [selectedDate]);

  const handleTriggerPipelineClick = () => {
    setShowPipelineModal(true);
  };

  const handlePipelineTriggered = (date: string, runId: string) => {
    // Optionally reload insights if pipeline was triggered for current date
    if (date === selectedDate && onInsightsReload) {
      // Wait a bit before reloading to let the pipeline start
      setTimeout(() => {
        onInsightsReload();
      }, 2000);
    }
  };


  return (
    <>
      <header className="header">
        <div className="header-top">
          <h1>Brief Studio</h1>
          <div className="header-actions">
            <button
              className="header-btn-base info-btn"
              onClick={() => setShowHowItWorksModal(true)}
            >
              How It Works
            </button>
            <button 
              className="regenerate-btn trigger-pipeline-btn"
              onClick={handleTriggerPipelineClick}
              title="Trigger Pipeline for Current Date"
            >
              Trigger Pipeline
            </button>
          </div>
        </div>
      
      <div className="toggle-buttons">
        <button className="toggle-btn active">Individual</button>
        <button className="toggle-btn">Team</button>
      </div>
      
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="date-select">Date:</label>
          <select
            id="date-select"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className={`filter-select ${dateChanged ? 'date-changed' : ''}`}
            disabled={isLoadingDates || availableDates.length === 0}
          >
            <option value="">
              {isLoadingDates ? 'Loading dates…' : 'Select Date'}
            </option>
            {availableDates.map((dateSummary) => (
              <option key={dateSummary.date} value={dateSummary.date}>
                {formatDateLabel(dateSummary.date)} ({dateSummary.user_count} users)
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="user-select">User:</label>
          <select 
            id="user-select"
            value={selectedUserId} 
            onChange={(e) => onUserChange(e.target.value)}
            className="filter-select"
            disabled={isLoadingUsers || availableUsers.length === 0}
          >
            <option value="">
              {isLoadingUsers ? 'Loading users…' : 'Select User'}
            </option>
            {availableUsers.map(user => (
              <option key={user.user_id} value={user.user_id}>
                {userLabelMap.get(user.user_id)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(datesError || usersError) && (
        <div className="inventory-error">
          {datesError && <p>Dates unavailable: {datesError}</p>}
          {usersError && <p>Users unavailable: {usersError}</p>}
        </div>
      )}

      {inventoryMeta && (inventoryMeta.datesGeneratedAt || inventoryMeta.usersGeneratedAt) && (
        <div className="inventory-meta">
          <span>
            Snapshot: {formatTimestamp(inventoryMeta.usersGeneratedAt || inventoryMeta.datesGeneratedAt)}
          </span>
          <span>
            Users loaded: {inventoryMeta.userCount ?? availableUsers.length}
          </span>
        </div>
      )}
    </header>

    {/* Airflow Pipeline Modal */}
    <AirflowPipelineModal
      isOpen={showPipelineModal}
      onClose={() => setShowPipelineModal(false)}
      selectedDate={selectedDate}
      selectedUserId={selectedUserId}
      onPipelineTriggered={handlePipelineTriggered}
      availableUsers={availableUsers}
    />

    <HowItWorksModal
      isOpen={showHowItWorksModal}
      onClose={() => setShowHowItWorksModal(false)}
    />


    </>
  );
};

export default Header;
